import codeUtil from "@plannel/pages/utils/code-util";
import { useState, useEffect } from "react";
import { FormControl, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { withTranslation, useTranslation } from "react-i18next";
import dateUtils from "@plannel/utils/date-util";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import debug from "@plannel/utils/debug-util";

import { addMonths, addWeeks, addYears } from "date-fns";
import { isEqual, size, values } from "lodash";

import planConfigService from "@plannel/services/data/plan-config-service";
const PlanHorizonSelect = ({ selectSize = "medium", dtfDisable, version = {}, currentYN, configObj = {}, getValues, hasDTF = true }) => {
  const { t } = useTranslation();
  const selectWidth = hasDTF ? "33%" : "50%";
  const currentDate = new ZDate();
  const { MONTH, QUARTER } = codeUtil.buckets;
  const { module: { DP }, buckets, dpConfig, dayOfWeek } = codeUtil;
  const START_MIN_DATE = new ZDate(currentDate.getFullYear() - 10, 0, 0);
  const END_MAX_DATE = new ZDate(currentDate.getFullYear() + 10, 0, 0);

  const [fromDate, setFromDate] = useState(new ZDate());  const [isLoading, setIsLoading] = useState(true);
  const [planningRange, setPlanningRange] = useState(12);
  const [toDate, setToDate] = useState(currentDate);
  const [dtf, setDTF] = useState(0);
  const [dtfError, setDtfError] = useState(false);

  const [bucketUnit, setBucketUnit] = useState(MONTH);
  const [startDay, setStartDay] = useState();

  const changeDtf = value => {
    const newValue = parseInt(value, 10);
    if (newValue < 0) {
      setDtfError(true);
      setDTF(0);
    } else {
      setDtfError(false);
      setDTF(newValue);
    }
  };

  const changeBucketUnit = value => {
    if (codeUtil.isWeekBucket(value)) {
      setFromDate(currentDate);
      setToDate(addWeeks(currentDate, 4));
    } else if (isEqual(value, MONTH)) {
      setToDate(addMonths(currentDate, 1));
    } else if (isEqual(value, QUARTER)) {
      setToDate(addMonths(currentDate, 3));
    } else {
      setToDate(addYears(currentDate, 1));
    }
  };

  const initialPlanConfig = () => {
    const param = {
      moduleCd: DP,
      activeFlg: true
    };

    planConfigService.getAll(param)
      .then(response => {
        if (response.data?.results) {
          const resultData = response.data.results;
          for (const result of resultData) {
            if (isEqual(result.configCd, dpConfig.BUCKET_UNIT)) {
              setBucketUnit(result.strVal);
            } else if (isEqual(result.configCd, dpConfig.START_DAY)) {
              setStartDay(result.strVal);
            }
          }
        } else {
          setBucketUnit(buckets.MONTH);
          setStartDay(dayOfWeek.MONDAY.cd);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const init = () => {
    if (size(version) > 0) {
      const { PLANNING_RANGE, BUCKET_UNIT, PLAN_START_DATE, DTF, START_DAY } = version.planConfigObj;
      const range = PLANNING_RANGE ?? 0;
      const unit = BUCKET_UNIT ?? bucketUnit;
      const startDate = PLAN_START_DATE ?? fromDate;
      const planDtf = DTF ?? dtf;
      const endDate = dateUtils.calculateRangeDate(startDate, unit, START_DAY, range, true);
      setFromDate(startDate);
      setDTF(planDtf);
      setToDate(endDate);
      getValues([startDate, endDate, planDtf]);
      setPlanningRange(range);
    } else {
      const endDate = new ZDate(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      setToDate(dateUtils.nearDay(endDate, startDay, bucketUnit));
      setFromDate(dateUtils.nearDay(fromDate, startDay, bucketUnit));
      changeBucketUnit(bucketUnit);
      setPlanningRange(12);
      getValues([fromDate, endDate, dtf]);
    }
    setIsLoading(false);
  };

  const getWeekStartDate = (curDate, code) => {
    const weekDay = values(dayOfWeek).filter(week => isEqual(week.cd, startDay))[0];
    const day = curDate.getDay();

    let convertFlg = WorkbenchUtil.ableDayOfWeek(curDate, bucketUnit, startDay);
    let computeDays = Number(weekDay.id);

    if (isEqual(code, "end")) {
      convertFlg = WorkbenchUtil.ableEndDayOfWeek(curDate, bucketUnit, startDay);
      computeDays = Number(Number(weekDay.id) + 6);
    }

    if (convertFlg) {
      const convertDate = Number(curDate.getDate() - day + computeDays);
      const monthAllDays = new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, 0).getDate();
      if (convertDate > monthAllDays) {
        return new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, convertDate - monthAllDays);
      }
      return new ZDate(curDate.setDate(convertDate));
    }
    return curDate;
  };

  const setConfig = () => {
    const { PLANNING_RANGE, DTF, START_DAY } = configObj;
    const currentFirstDate = new ZDate(currentDate.getFullYear(), currentDate.getMonth(), 1);
    if (bucketUnit && codeUtil.isWeekBucket(bucketUnit)) {
      const weekStartDate = getWeekStartDate(currentDate, true);
      setFromDate(weekStartDate);
    } else {
      setFromDate(currentFirstDate);
    }
    setDTF(DTF?.numVal ?? 0);
    const range = PLANNING_RANGE?.numVal ?? 6;
    const currentEndDate = bucketUnit && codeUtil.isWeekBucket(bucketUnit) ? currentDate : currentFirstDate;
    const endDate = dateUtils.calculateRangeDate(currentEndDate, bucketUnit, START_DAY?.strVal, range, true);
    setToDate(endDate);
    setPlanningRange(range);
    getValues([currentFirstDate, endDate, DTF?.numVal ?? 0]);
  };

  useEffect(() => {
    if (isLoading) {
      initialPlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (bucketUnit) {
      init();
    }
       // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit]);

  useEffect(() => {
    if (size(configObj) > 0) {
      setConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configObj]);

  useEffect(() => {
    if (fromDate) {
      const endDate = dateUtils.calculateRangeDate(new ZDate(fromDate), bucketUnit, startDay, planningRange, true);
      setToDate(endDate);
      getValues([fromDate, endDate, dtf]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate]);

  useEffect(() => {
    if (toDate) {
      if (new ZDate(fromDate) > new ZDate(toDate)) {
        setToDate(new ZDate(fromDate));
      }
      getValues([fromDate, toDate, dtf]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toDate]);

  useEffect(() => {
    if (startDay) {
      setFromDate(getWeekStartDate(fromDate, "start"));
      setToDate(getWeekStartDate(toDate, "end"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDay]);

  useEffect(() => {
    if (bucketUnit) {
      if (startDay && codeUtil.isWeekBucket(bucketUnit)) {
        setFromDate(getWeekStartDate(fromDate, "start"));
        setToDate(getWeekStartDate(toDate, "end"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      disabled={currentYN}>
      <FormControl sx={{ width: selectWidth }}>
        <DatePicker
          sx={{ mt: 1, mb: 0.5, height: "40px" }}
          label={codeUtil.isWeekBucket(bucketUnit) ? t("planStartDate") : t("planStartMonth")}
          format={codeUtil.isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
          views={codeUtil.isWeekBucket(bucketUnit) ? ["year", "month", "day"] : ["year", "month"]}
          minDate={START_MIN_DATE}
          value={fromDate}
          shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, bucketUnit, startDay)}
          shouldDisableMonth={param => {
            const paramMon = new ZDate(param).getMonth() + 1;
            const disableMons = ["2", "3", "4", "6", "7", "8", "10", "11"];
            return isEqual(bucketUnit, QUARTER) ? disableMons.includes(paramMon.toString()) : false;
          }}
          onAccept={newValue => {
            const startDate = newValue ?? fromDate;
            setFromDate(startDate);
          }}
          slotProps={{ textField: { size: selectSize } }}
          disabled={currentYN}
        />
      </FormControl>
      {hasDTF ? (
        <FormControl sx={{ width: "32%", ml: "1%" }}>
          <TextField
            size={selectSize}
            label={t("dtf")}
            value={dtf}
            type="number"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]+" }}
            sx={{ textAlign: "center" }}
            error={dtfError}
            onChange={e => {
              changeDtf(e.target.value);
              getValues([fromDate, toDate, parseInt(e.target.value, 10)]);
            }}
            disable={dtfDisable}
          />
        </FormControl>
      ) : null}
      <FormControl sx={{ width: selectWidth, ml: "1%" }}>
        <DatePicker
          label={codeUtil.isWeekBucket(bucketUnit) ? t("planEndDate") : t("planEndMonth")}
          format={codeUtil.isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
          views={codeUtil.isWeekBucket(bucketUnit) ? ["year", "month", "day"] : ["year", "month"]}
          sx={{ mt: 1, mb: 0.5, height: "40px" }}
          value={toDate}
          minDate={fromDate}
          maxDate={END_MAX_DATE}
          shouldDisableDate={(e) => WorkbenchUtil.ableEndDayOfWeek(e, bucketUnit, startDay)}
          shouldDisableMonth={param => {
            const paramMon = new ZDate(param).getMonth() + 1;
            const disableMons = ["1", "2", "3", "5", "6", "7", "10", "11"];
            return isEqual(bucketUnit, QUARTER) ? disableMons.includes(paramMon.toString()) : false;
          }}
          onAccept={newValue => {
            const endDate = newValue ?? toDate;
            setToDate(endDate);
          }}
          slotProps={{ textField: { size: selectSize } }}
        />
      </FormControl>
    </LocalizationProvider>
  );
};
export default withTranslation()(PlanHorizonSelect);
